<template>
  <section class="section">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ General.PROFILES }}
      </h1>
      <div class="header-buttons column">
        <b-button
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-py-15 spacing-mr-5"
          :expanded="isMobile"
          @click="goToPermissions"
          >{{ App.ProfilesScreen.PERMISSIONS_MANAGEMENT }}</b-button
        >
        <b-button
          iconLeft="plus"
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-py-15 spacing-mr-5"
          tag="router-link"
          :expanded="isMobile"
          :to="newProfile()"
          >{{ App.ProfilesScreen.NEW_PROFILE }}</b-button
        >
      </div>
    </div>

    <div class="is-flex">
      <GenericPanelSearch
        class="flex-1"
        :filterList="filterTags"
        :filterOptions="getFilterOptions"
        :tabPersistName="tabPersistName"
        :placeholderInput="App.ProfilesScreen.PLACEHOLDERS.SEARCH_BY"
        @filters="setFilterList"
        @search-value="setSearchValue"
      />
    </div>

    <div class="level-left has-text-left m-10 mt-30">
      <span class="has-text-color-7 has-text-weight-semibold">
        {{ getPaginationDetails }}
      </span>
    </div>

    <section class="spacing-mb-20">
      <b-table
        class="custom-table-administration custom-4-columns-part-address"
        :data="getProfiles.results"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSortDirection="defaultSortOrder"
        :defaultSort="[sortField, sortOrder]"
        backendSorting
        type="is-black"
        hoverable
      >
        <template>
          <b-table-column v-slot="props" :label="General.PROFILE" field="name">
            <div class="has-text-color-8">
              {{ props.row.name }}
            </div>
          </b-table-column>
          <b-table-column v-slot="props" :label="General.USERS">
            <b-button
              class="select-button has-text-color-8"
              :class="{ 'is-align-items-flex-start': isMobile }"
              :disabled="!props.row.users_count"
              tag="router-link"
              :to="goToSearchUsers(props.row)"
            >
              {{ props.row.users_count }}
              {{ props.row.users_count === 1 ? General.USER : General.USERS }}
            </b-button>
          </b-table-column>
          <b-table-column v-slot="props" :label="App.ProfilesScreen.ACTIVE">
            <b-switch
              :value="props.row.active"
              @input="event => setActive(props.row.id, event)"
            ></b-switch>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="isMobile ? '' : General.ACTIONS"
          >
            <ActionsGrouper :actions="actionList(props.row)" />
          </b-table-column>
        </template>

        <template #empty>
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>
      </b-table>
    </section>

    <div
      class="is-flex mb-40"
      :class="{ 'is-flex-direction-column-reverse': isMobile }"
    >
      <b-dropdown
        v-model="perPage"
        aria-role="list"
        position="is-top-right"
        :class="{ 'spacing-mb-40': isMobile }"
        :expanded="isMobile"
        @change="currentPage = 1"
      >
        <template slot="trigger">
          <b-button class="custom-dropdown-button" :expanded="isMobile">
            {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}

            <b-icon class="spacing-mx-5" icon="chevron-down" />
          </b-button>
        </template>
        <div class="spacing-p-5 has-text-centered">
          {{ General.RECORDS_FOR_PAGE }}
          <div class="list-page">
            <b-dropdown-item
              v-for="option in optionsPagination"
              :key="option"
              :value="option"
            >
              <div>
                {{ option }}
              </div>
            </b-dropdown-item>
          </div>
        </div>
      </b-dropdown>

      <div class="custom-pagination-container" :class="{ 'mb-10': isMobile }">
        <b-pagination
          :total="getProfiles.count"
          :perPage="perPage"
          :current.sync="currentPage"
          rangeBefore="1"
          rangeAfter="1"
          order="is-centered"
          class="process-panel-pagination"
          rounded
          :iconPrev="prevIcon"
          :iconNext="nextIcon"
          :aria-next-label="General.NEXT_PAGE"
          :aria-previous-label="General.PREVIOUS_PAGE"
          :aria-page-label="General.PAGE"
          :aria-current-label="General.CURRENT_PAGE"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapGetters } from "vuex";
import { App, General } from "@/utils/constants";
import NoListMessage from "@/components/NoListMessage";
import ModalWarning from "@/views/App/ModalWarning.vue";
import {
  USER_PERMISSIONS as userPermissions,
  TAB_PERSIST_NAME as PERSIST_NAME
} from "@/utils/enumerate";
import { formatPagination } from "@/utils/transformers";
import GenericPanelSearch from "@/components/GenericPanelSearch";

export default {
  components: {
    NoListMessage,
    GenericPanelSearch
  },
  data() {
    return {
      App,
      General,
      isLoading: false,
      isFetching: false,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      defaultSortOrder: "desc",
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      sortField: "name",
      sortOrder: "desc",
      searchValue: "",
      filterTags: {}
    };
  },
  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getProfiles: "getProfiles",
      optionsPagination: "getItensPage",
      hasPermission: "hasPermission",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    getPaginationDetails() {
      return formatPagination(this.getProfiles, this.currentPage, this.perPage);
    },

    requestBaseData() {
      return {
        page: this.currentPage,
        paginator_count: this.perPage,
        search: this.searchValue,
        order_by: this.sortField,
        permissions: this.filterTags?.permissions?.list?.map(item => item.id),
        profile_assigner: this.filterTags?.profiles?.id,
        list: true
      };
    },

    getFilterOptions() {
      return {
        PERMISSIONS: {
          name: General.PERMISSIONS,
          type: "PERMISSIONS",
          key: "permissions",
          unique: true
        },
        PROFILES: {
          name: General.ASSIGNER_PROFILE,
          type: "PROFILES",
          key: "profiles",
          unique: true
        }
      };
    },

    tabPersistName() {
      return `${PERSIST_NAME.PROFILES}_${this.user.id}`;
    }
  },
  watch: {
    requestBaseData: {
      handler() {
        this.requestProfileList();
      }
    }
  },
  beforeMount() {
    this.requestProfileList();
  },
  methods: {
    ...mapActions({
      profileRequest: "profileRequest",
      deleteProfileRequest: "deleteProfileRequest",
      activeProfileRequest: "activeProfileRequest"
    }),

    setSearchValue: debounce(async function(valueForSearch) {
      this.isFetching = true;
      this.searchValue = valueForSearch;
      if (this.currentPage != 1) this.currentPage = 1;
      this.isFetching = false;
    }, 1000),

    setFilterList(values) {
      this.filterTags = values;
      if (this.currentPage != 1) this.currentPage = 1;
    },

    goToPermissions() {
      if (this.hasPermission(userPermissions["MANAGE_PERMISSIONS"])) {
        this.$router.push("/app/admin/permissoes-perfis");
      }
    },

    newProfile() {
      return `editor-perfil`;
    },

    goToProfile(id = false) {
      return `editor-perfil${typeof id === "number" ? "/" + id : ""}`;
    },

    goToSearchUsers(profile) {
      return {
        name: "Usuários",
        params: {
          filterProfile: [
            {
              id: profile.id,
              value: profile.name,
              displayText: profile.name,
              type: { key: "profiles", name: "Perfil" }
            }
          ]
        }
      };
    },

    requestProfileList: debounce(async function() {
      this.isLoading = true;
      await this.profileRequest(this.requestBaseData);
      this.isLoading = false;
    }, 1000),

    callDialog(index) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.CONFIRM,
          message: this.App.ProfilesScreen.ARE_YOU_SURE_DELETE_PROFILE,
          confirm: () => this.deleteProfile(index)
        },
        buttons: [
          { label: General.YES, isConfirm: false, action: true },
          { label: General.NO, isConfirm: true, action: false }
        ],
        hasModalCard: true,
        trapFocus: true
      });
    },

    async deleteProfile(profileId) {
      this.isLoading = true;
      const status = await this.deleteProfileRequest(profileId);
      if (status) {
        if (this.getProfiles.results.length <= 1)
          if (this.currentPage != 1) this.currentPage--;
        await this.requestProfileList();
      }
      this.isLoading = false;
    },

    async setActive(profileId, active = true) {
      this.isLoading = true;
      await this.activeProfileRequest({ profileId, active });
      this.requestProfileList();
      this.isLoading = false;
    },

    actionList(item) {
      return [
        {
          label: this.General.EDIT,
          icon: "square-edit-outline",
          link: this.goToProfile(item.id)
        },
        {
          label: this.General.DELETE,
          icon: "delete",
          validated: !item.users_count,
          callback: () => this.callDialog(item.id)
        }
      ];
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding-top: 20px;

  .header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    @media screen and (max-width: $tablet) {
      flex-direction: column;
      gap: 5px;
    }
  }

  .select-button {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    text-decoration: underline;
  }

  .search-field {
    display: flex;

    .filter-button {
      margin-right: 5px;
    }

    @media screen and (max-width: $tablet) {
      flex-direction: column;
      gap: 5px;

      .filter-button {
        margin-right: 0;
      }
    }
  }
}
</style>
